import _ from 'underscore';

import { navRefreshPageIds, getSiteMapItemRoute } from '@bingads-webui-react/with-site-map';
import { SideNavigationItemProps, SideNavigationSecondaryItemProps } from '@bingads-webui-fluent-component/navigation';

import {
  I18n, IActivityContext, Scope, SitemapItem, UserContext,
} from '../types';
import {
  AllToolsGroupItemIds,
  AllToolsId,
  CreateGroupItemIds,
  FallbackIcon,
  NavItemsIconMapping,
  StaticNavItems,
} from '../consts';
import { getPinedItems } from './preference-processor';

type CustomNavigationItemProps = { [key: string]: Partial<SideNavigationItemProps> };

interface GetNavigationGroupItemsConfigParams {
  i18n: I18n;
  items: SitemapItem[];
  scope: Scope;
  userContext: UserContext;
  activityContext: IActivityContext;
  customItemProps?: CustomNavigationItemProps;
}

const PrimaryNavigationActivity = 'primary-navigation';

const getNavigationGroupItemsConfig = (params: GetNavigationGroupItemsConfigParams): SideNavigationItemProps[] => {
  const {
    i18n,
    items,
    scope,
    userContext,
    activityContext,
    customItemProps = {},
  } = params;

  return _.chain(items)
    .filter((item) => !_.isUndefined(item))
    .filter((item) => (item.fixed || item.pinned || item.selected) && item.searchable !== false)
    .map((item) => {
      const customProps = customItemProps[item.id];

      const secondaryNavItems: SideNavigationSecondaryItemProps[] = _.chain(item.children)
        .filter((child) => child.searchable !== false)
        .map((child) => ({
          key: child.id,
          id: `popover-${child.id}`,
          text: i18n.getString(child.name),
          onClick: () => {
            if (child.selected) {
              return;
            }

            const { pathName: pathname, search } = getSiteMapItemRoute({ item: child, scope, userContext });

            activityContext.signal(`popover clicked: ${child.id}`, PrimaryNavigationActivity);
            scope.history.push({ pathname, search });
          },
        })).value();

      const isTemporary = !item.fixed && item.selected && !item.pinned;
      // If the item isn't pinned we need to show the secondary nav to allow pinning it
      // Otherwise, we only show the secondary nav if there are more than one children
      const shouldShowSecondaryNav = isTemporary || _.size(secondaryNavItems) > 1;

      const itemProps: SideNavigationItemProps = {
        key: item.id,
        id: item.id,
        text: i18n.getString(item.name),
        icon: NavItemsIconMapping[item.id] || FallbackIcon,

        isSelected: item.selected,
        isTemporary,

        onClick: () => {
          if (item.selected) {
            return;
          }

          const { pathName: pathname, search } = getSiteMapItemRoute({ item, scope, userContext });

          activityContext.signal(`item clicked: ${item.id}, ${item.name}`, PrimaryNavigationActivity);
          scope.history.push({ pathname, search });
        },
        onPin: () => item.onPinToggle(PrimaryNavigationActivity),

        ...(shouldShowSecondaryNav && { secondaryNavItems: secondaryNavItems.length > 1 ? secondaryNavItems : [] }),
        ...(!_.isEmpty(customProps) && customProps),
      };

      return itemProps;
    })
    .value();
};

const filterMainGroupItems = (items: SitemapItem[]): SitemapItem[] => _.filter(items, (item) => !_.contains(CreateGroupItemIds, item.id) && !item.externalLink);

interface GetNavigationItemsConfigParams {
  i18n: I18n;
  siteMap: SitemapItem[];
  scope: Scope;
  userContext: UserContext;
  activityContext: IActivityContext;
  onOpenAllTools: () => void;
  preferenceService: any;
  permissions: any;
}

export const getNavigationItemsConfig = (params: GetNavigationItemsConfigParams): SideNavigationItemProps[][] => {
  const {
    i18n,
    siteMap,
    scope,
    userContext,
    activityContext,
    permissions,
  } = params;

  const head = _.filter(siteMap, (item) => item.fixed && !item.tail);
  const tail = _.filter(siteMap, (item) => item.fixed && item.tail);
  const pinedItemsId = getPinedItems(params.preferenceService, permissions);
  // get pinned items in the order of preference
  const pinned = [];
  _.each(pinedItemsId, (id) => {
    const item = _.findWhere(siteMap, { id });
    if (item) {
      pinned.push(item);
    }
  });
  const selected = _.filter(siteMap, (item) => item.selected && !item.fixed && !item.pinned);

  const menuItems = [...head, ...pinned, ...selected, ...tail];

  const createGroup = getNavigationGroupItemsConfig({
    i18n,
    items: _.filter(menuItems, (item) => _.contains(CreateGroupItemIds, item.id)),
    scope,
    userContext,
    activityContext,
    customItemProps: {
      [navRefreshPageIds.create]: {
        isPrimary: true,
        isSelected: false,
      },
    },
  });

  const mainGroup = getNavigationGroupItemsConfig({
    i18n,
    items: filterMainGroupItems(menuItems),
    scope,
    userContext,
    activityContext,
  });

  const allToolsGroup = getNavigationGroupItemsConfig({
    i18n,
    items: _.filter(StaticNavItems, (item) => _.contains(AllToolsGroupItemIds, item.id)),
    scope,
    userContext,
    activityContext,
    customItemProps: {
      [AllToolsId]: {
        isTrailing: true,
        isPermanent: true,
        onClick: () => {
          activityContext.signal('item clicked: tools', PrimaryNavigationActivity);
          params.onOpenAllTools();
        },
      },
    },
  });

  return [createGroup, mainGroup, allToolsGroup];
};
